import { z } from "zod";
// The contract of the beta control routes (CHT-780; docs/operations/beta/control-inventory.md):
//
//   GET  /staff/operations/beta-controls               the state of both controls and the size of the cohort (`operations.jobs.read`)
//   PUT  /staff/operations/beta-controls/:key          engage or release one control (`operations.jobs.manage`, `Idempotency-Key`)
//   GET  /staff/operations/beta-cohort                 the members of the cohort, as opaque ids (`operations.jobs.read`)
//   PUT  /staff/operations/beta-cohort/:customerId     add, move or remove one customer (`operations.jobs.manage`, `Idempotency-Key`)
//
// Every shape is STRICT. A view holds ids, closed codes, counts, versions and times: no name, no email and no free text. This package
// cannot import the domain package: the tuples below repeat the domain's (`beta-controls.ts`); `apps/api` has a parity test.
export const STAFF_OPERATIONS_BETA_CONTROLS_PATH = "/staff/operations/beta-controls";
export const STAFF_OPERATIONS_BETA_CONTROL_PATH = "/staff/operations/beta-controls/:key";
export const STAFF_OPERATIONS_BETA_COHORT_PATH = "/staff/operations/beta-cohort";
export const STAFF_OPERATIONS_BETA_COHORT_MEMBER_PATH = "/staff/operations/beta-cohort/:customerId";
export const staffOperationsControlKeys = ["AI_PAUSE", "COHORT_GATE"];
export const staffOperationsBetaReasons = [
    "INCIDENT",
    "PROVIDER_OUTAGE",
    "COST",
    "MAINTENANCE",
    "BETA_SCOPE",
    "PROOF",
];
const responseInstant = z.union([
    z.date(),
    z.iso.datetime({ offset: true }).transform((value) => new Date(value)),
]);
// --- request -----------------------------------------------------------------------------------------
export const staffOperationsControlKeySchema = z.enum(staffOperationsControlKeys);
/** Engaging needs a closed reason. A release names none. `expectedVersion` is the `version` of the view the operator read. */
export const staffOperationsBetaControlBodySchema = z.discriminatedUnion("engaged", [
    z.strictObject({
        engaged: z.literal(true),
        reasonCode: z.enum(staffOperationsBetaReasons),
        expectedVersion: z.number().int().min(0),
    }),
    z.strictObject({ engaged: z.literal(false), expectedVersion: z.number().int().min(0) }),
]);
/** `cohortId: null` removes the customer from the cohort. */
export const staffOperationsBetaCohortMemberBodySchema = z.strictObject({
    cohortId: z
        .string()
        .regex(/^[a-z0-9][a-z0-9-]{0,39}$/)
        .nullable(),
});
export const staffOperationsBetaCohortQuerySchema = z.strictObject({
    cursor: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
});
// --- response ----------------------------------------------------------------------------------------
const controlViewSchema = z.strictObject({
    engaged: z.boolean(),
    /** `0` while the control has no row. */
    version: z.number().int().min(0),
    reasonCode: z.enum(staffOperationsBetaReasons).nullable(),
    changedAt: responseInstant.nullable(),
});
export const staffOperationsBetaControlsSchema = z.strictObject({
    generatedAt: responseInstant,
    aiPause: controlViewSchema,
    cohortGate: controlViewSchema,
    cohort: z.strictObject({ memberCount: z.number().int().min(0) }),
});
export const staffOperationsBetaControlResultSchema = z.strictObject({
    controlKey: staffOperationsControlKeySchema,
    engaged: z.boolean(),
    version: z.number().int().min(1),
});
export const staffOperationsBetaCohortPageSchema = z.strictObject({
    members: z.array(z.strictObject({ customerId: z.uuid(), cohortId: z.string(), addedAt: responseInstant })),
    nextCursor: z.uuid().nullable(),
});
export const staffOperationsBetaCohortMemberResultSchema = z.strictObject({
    member: z.boolean(),
    changed: z.boolean(),
});
